import { useMemo } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { ListEnvelope } from '../../../shared/api/genexus-query';
import type { CrudApi, CrudHooks } from '../../../shared/api/crud-hooks';
import type { DataTableQuery } from '../../../shared/components/DataTable';
import { createCompanyAddressesApi } from './api';
import type {
  CepLookupResult,
  CompanyAddressDto,
  CompanyAddressInput,
  CompanyAddressListItem,
} from './types';

/**
 * Hooks CRUD para endereços de uma Empresa específica. Espelha o pattern de
 * `useClientProjectsHooks` em `clientes/projects/api.ts` — wiring manual com
 * `useMemo` porque a `queryKey` precisa incluir `companyId`.
 */
export function useCompanyAddressesHooks(companyId: number): CrudHooks<
  CompanyAddressListItem,
  CompanyAddressDto,
  CompanyAddressInput
> & {
  api: CrudApi<CompanyAddressListItem, CompanyAddressDto, CompanyAddressInput> & {
    lookupCep: (cep: string) => Promise<CepLookupResult>;
  };
  useLookupCep: () => UseMutationResult<CepLookupResult, Error, string>;
} {
  const addressesApi = useMemo(() => createCompanyAddressesApi(companyId), [companyId]);
  const queryKey = useMemo(() => ['companies', companyId, 'addresses'] as const, [companyId]);
  const qc = useQueryClient();

  const useList = (
    query: DataTableQuery,
  ): UseQueryResult<ListEnvelope<CompanyAddressListItem>, Error> =>
    useQuery({
      queryKey: [...queryKey, 'list', query],
      queryFn: () => addressesApi.list(query),
      placeholderData: (previous) => previous,
    });

  const useDetail = (id: number | null | undefined): UseQueryResult<CompanyAddressDto, Error> =>
    useQuery({
      queryKey: [...queryKey, 'detail', id],
      queryFn: () => addressesApi.get(id as number),
      enabled: id !== null && id !== undefined,
    });

  const useCreate = (): UseMutationResult<CompanyAddressDto, Error, CompanyAddressInput> =>
    useMutation({
      mutationFn: (input) => addressesApi.create(input),
      onSuccess: () => qc.invalidateQueries({ queryKey }),
    });

  const useUpdate = (): UseMutationResult<
    CompanyAddressDto,
    Error,
    { id: number; input: CompanyAddressInput }
  > =>
    useMutation({
      mutationFn: ({ id, input }) => addressesApi.update(id, input),
      onSuccess: () => qc.invalidateQueries({ queryKey }),
    });

  const useDelete = (): UseMutationResult<void, Error, number> =>
    useMutation({
      mutationFn: (id) => addressesApi.remove(id),
      onSuccess: () => qc.invalidateQueries({ queryKey }),
    });

  /**
   * Lookup ViaCEP — não é CRUD, não invalida cache. Usado pelo botão "Buscar CEP"
   * do form de endereço. Erro 404 (`CEP_NAO_ENCONTRADO`) propaga para o consumer.
   */
  const useLookupCep = (): UseMutationResult<CepLookupResult, Error, string> =>
    useMutation({
      mutationFn: (cep) => addressesApi.lookupCep(cep),
    });

  return { api: addressesApi, useList, useDetail, useCreate, useUpdate, useDelete, useLookupCep };
}
